import {
  useRef, useEffect, useState, useContext,
  MouseEventHandler
} from 'react';
import _ from 'lodash';
import { getOffset, getScrollPositionX, getScrollPositionY } from '../utils/utils';
import GlobalContext from '../contexts/GlobalContext';
import { ORDER } from '../types/dart';

const useEventListener = (
  eventName: string,
  handler: Function,
  element: Document | Element
): void => {
  // Create a ref that stores handler
  const savedHandler = useRef(null);

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    // Make sure element supports addEventListener
    const isSupported = element && element.addEventListener;

    if (!isSupported) {
      return;
    }

    // Create event listener that calls handler function stored in ref
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const eventListener = (event: any): void => savedHandler.current(event);

    // Add event listener
    element.addEventListener(eventName, eventListener);

    // Remove event listener on cleanup
    // eslint-disable-next-line consistent-return
    return (): void => {
      element.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element]);
};
const useScroll = (throttle = 100, elem: HTMLElement): void => {
  const scrollThrottle = useRef(throttle);
  const [, setScroll] = useState({
    hasScrolled: false,
    posX: 0,
    posY: 0,
    prevX: 0,
    prevY: 0
  });

  useEffect(() => {
    if (elem) {
      scrollThrottle.current = throttle;
    }
  }, [throttle, elem]);

  useEffect(() => {
    if (!elem) {
      return;
    }

    const updateScroll = (): void => {
      const curX = getScrollPositionX();
      const curY = getScrollPositionY();

      setScroll((oldScroll) => ({
        hasScrolled: true,
        posX: elem.scrollLeft + curX - oldScroll.prevX,
        posY: elem.scrollTop + curY - oldScroll.prevY,
        prevX: curX,
        prevY: curY
      }));
    };
    const scrollListener = _.throttle(updateScroll, scrollThrottle.current);

    document.addEventListener('scroll', scrollListener);

    // eslint-disable-next-line consistent-return
    return (): void => {
      document.removeEventListener('scroll', scrollListener);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elem]);
};
const useSubmitFormWithReCaptcha = (): Function => {
  const { globalContext } = useContext(GlobalContext);

  function submitFormWithReCaptcha(method, values): void {
    const recaptchaSiteKey = globalContext.gb.liquid.recaptchaSiteKey;
    var useRecaptchaEnterprise = globalContext.gb.liquid.settings.use_recaptcha_enterprise;
    const caastleTestToken = globalContext.gb.getCookie('caastle-test-token');
    let grecaptcha = window.grecaptcha;
    let grecaptchaMethod;

    if (caastleTestToken || typeof grecaptcha === 'undefined') {
      values.clientToken = caastleTestToken;
      values.clientTokenType = 'CaasCaptcha';

      return ((): void => method(values))();
    }

    if (useRecaptchaEnterprise) {
      grecaptchaMethod = grecaptcha && grecaptcha.enterprise;
      values.clientTokenType = 'RecaptchaEnterprise';
    } else {
      grecaptchaMethod = grecaptcha;
      values.clientTokenType = 'recaptcha';
    }

    return grecaptchaMethod && grecaptchaMethod.ready(function () {
      return grecaptchaMethod && grecaptchaMethod.execute(recaptchaSiteKey, { action: values && values.action })
        .then(function (token) {
          values.clientToken = token;

          return ((): void => method(values))();
        })
        .catch(function (error) {
          throw error;
        });
    });
  }

  return submitFormWithReCaptcha;
};

export type IClockOptions = {
  startTime: number;
  order: string;
  formatTime?: (sec: number) => string;
};

const useClock = (options: IClockOptions) => {
  const { startTime, order = ORDER.ASC } = options || {};
  const [seconds, setSeconds] = useState(startTime);
  const [isRunning, setIsRunning] = useState(true);

  useEffect(() => {
    let intervalId;

    if (isRunning) {
      intervalId = setInterval(() => {
        setSeconds((prevSeconds) => (order === 'asc' ? prevSeconds + 1 : prevSeconds - 1));
      }, 1000);
    }

    return (): void => clearInterval(intervalId);
  }, [isRunning, order]);

  const reset = (): void => {
    setSeconds(startTime);
  };

  useEffect(() => {
    reset();
  }, [startTime]);

  const toggle = (): void => {
    setIsRunning((prevIsRunning) => !prevIsRunning);
  };
  const stop = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setIsRunning((__prevIsRunning) => false);
  };

  return {
    seconds: seconds,
    isRunning: isRunning,
    reset: reset,
    toggle: toggle,
    stop: stop,
    // eslint-disable-next-line no-restricted-globals
    formattedTime: typeof options.formatTime === 'function' && !isNaN(seconds) ? options.formatTime(seconds) : ''
  };
};
/**
 * custom hook to fire functions when someone clicks outside reference HTML element
 * @param ref reference element
 * @param callback function to be fired on outside click
 */
const useOutsideClickListener = (ref: any, callback: () => void): void => {
  /**
   * fires callback when user clicks outside reference element
   * @param event mousedown event
   */
  function handleOutsideClick(event: any): void {
    if (ref.current && !ref.current.contains(event.target)) {
      callback();
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);

    return (): void => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, callback]);
};
const useSJStickyBtnScroll = (): void => {
  const { globalContext } = useContext(GlobalContext);

  useEffect(() => {
    let handleStickyScroll;
    /**
    * Set scroll event for Get Started/Subscribe button to make it sticky
    */
    // eslint-disable-next-line complexity
    const handleSJStickyBtnScroll = (): void => {
      const settings = globalContext.gb.liquid.settings;
      const template = window.caastle.template;
      const indexPage = template === 'index';
      const hiwPage = template === 'page.howitworks';
      const dynamicLP = template === 'page.dynamic-lp';
      const enableGetStartedOnIndexPage = settings.show_get_started_button_on_index_page;
      const enableGetStartedOnHIWPage = settings.show_get_started_button_on_hiw_page;
      const nonStickyGetStartedOnIndexPage = !enableGetStartedOnIndexPage && indexPage;
      const nonStickyGetStartedOnHIWPage = !enableGetStartedOnHIWPage && hiwPage;
      const customer = globalContext.gb.customer || null;
      const isCustomerSubscriber = globalContext.gb.customer.isSubscriber();
      const isNotSubscriber = !customer || !isCustomerSubscriber;

      if ((isNotSubscriber && nonStickyGetStartedOnIndexPage) ||
        nonStickyGetStartedOnHIWPage || dynamicLP) {
        const mainSJHeaderElem = document.querySelector('#main-sj-header');

        mainSJHeaderElem.classList.add('sj-non-sticky');

        // for adjusting coaching Bar position
        const coachingBarElem = document.querySelector('.coaching-wrap') as HTMLElement;

        if (coachingBarElem) {
          coachingBarElem.style.top = '0px';
        }
      }

      if (!isCustomerSubscriber &&
        ((indexPage && !customer) || hiwPage || dynamicLP)) {
        const activateStickyButton = !window.location.hash;
        const getStartedWrapper = document.querySelector('.js-get-started-wrapper') as HTMLElement;
        const stickyWrapper = document.querySelector('.sj-sticky-wrapper') as HTMLElement;
        const styckyWrapperOffset = getOffset(stickyWrapper);
        const stickyOffsetTop = styckyWrapperOffset && styckyWrapperOffset.top;

        if (stickyWrapper) {
          stickyWrapper.style.height = `${getStartedWrapper.offsetHeight}px`;
        }

        if (activateStickyButton) {
          handleStickyScroll = (): void => {
            const windowTop = window.scrollY;

            if (stickyOffsetTop < windowTop) {
              getStartedWrapper.classList.add('sj-sticky');
            } else {
              getStartedWrapper.classList.remove('sj-sticky');
            }
          };

          window.addEventListener('scroll', handleStickyScroll);
        } else {
          getStartedWrapper.classList.remove('hide');
        }
      }
    };

    handleSJStickyBtnScroll();

    // eslint-disable-next-line consistent-return
    return (): void => {
      window.removeEventListener('scroll', handleStickyScroll);
    };
  }, []);
};
/**
 * custom hook to enable horizontal scroll on drag for desktop
 * @param ref reference element
 */
const useHorizontalScrollOnDrag = (elementRef: React.MutableRefObject<HTMLElement>): {
  handleDrag: MouseEventHandler<HTMLDivElement>;
  handleDragStart: MouseEventHandler<HTMLDivElement>;
} => {
  const [dragState, setDragState] = useState({
    isMouseDown: false,
    startX: 0,
    scrollLeft: 0
  });
  const handleDragStart = (e): void => {
    if (!elementRef.current) {
      return;
    }

    const slider = elementRef.current;
    const startX = e.pageX - slider.offsetLeft;
    const scrollLeft = slider.scrollLeft;

    setDragState({
      startX: startX,
      isMouseDown: true,
      scrollLeft: scrollLeft
    });
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';

    const handleDragEnd = (): void => {
      setDragState((prevState) => ({
        ...prevState,
        isMouseDown: false
      }));

      if (!elementRef.current) {
        return;
      }

      document.body.style.cursor = 'default';
      document.body.style.removeProperty('user-select');
      document.removeEventListener('mouseup', handleDragEnd);
    };

    document.addEventListener('mouseup', handleDragEnd);
  };
  const handleDrag = (e): void => {
    if (!dragState.isMouseDown || !elementRef) {
      return;
    }

    e.preventDefault();

    const slider = elementRef.current;
    const x = e.pageX - slider.offsetLeft;
    const walkX = (x - dragState.startX) * 1.5;

    slider.scrollLeft = dragState.scrollLeft - walkX;
  };

  return {
    handleDrag: handleDrag,
    handleDragStart: handleDragStart
  };
};

export { useClock };
export { useSubmitFormWithReCaptcha };

export { useScroll };
export { useEventListener };
export { useOutsideClickListener };
export { useSJStickyBtnScroll };
export { useHorizontalScrollOnDrag };
